import { getTunables } from '../config/tunables';
import { getDefaultPlayerTemplate, PlayerTemplateDefinition } from './PlayerTemplate';
import type { UpgradeId } from './UpgradeSystem';
import { normalizeDamageSource, RunStatsTracker, type RunBuildSnapshot, type RunPurchaseEvent, type RunStatsSnapshot, type RunDamageSource } from './RunStats';
import type { PartnerType } from './shopCatalog';

export type RunPhase = 'ready' | 'playing' | 'levelUp' | 'waveComplete' | 'victory' | 'dead';

export interface RunStats {
  damageDealt: number;
  damageTaken: number;
  hitsTaken: number;
  materialsPicked: number;
  enemiesDefeated: number;
  levelsGained: number;
}

export type DamageSource = 'collision' | 'shockwave' | 'boss-hazard' | 'boss-projectile' | 'minion';

export interface DeathStats {
  collisionHits: number;
  shockwaveHits: number;
  bossHazardHits: number;
  bossProjectileHits: number;
  minionHits: number;
  lastHitSource: DamageSource | 'none';
}

export interface UpgradeModifiers {
  damageBonus: number;
  attackRangeBonus: number;
  attackSpeedBonus: number;
  moveSpeedBonus: number;
  maxHpDelta: number;
  pickupRangeBonus: number;
  critChanceBonus: number;
}

export interface RunSnapshot {
  phase: RunPhase;
  seed: string;
  templateName: string;
  wave: number;
  maxWave: number;
  waveTimeLeft: number;
  maxHp: number;
  hp: number;
  materials: number;
  level: number;
  xp: number;
  nextLevelXp: number;
  stats: RunStats;
  deathStats: DeathStats;
  analysis: RunStatsSnapshot;
  pendingLevelUps: number;
  upgradeModifiers: UpgradeModifiers;
}

const MAX_HP = 100;
export const MAX_WAVE = 0;
const FIRST_LEVEL_XP = 8;
const LEVEL_XP_STEP = 5;

export interface RunRelicModifiers {
  maxHpDelta: number;
  materialGainBonus: number;
  xpGainBonus: number;
}

export class RunState {
  private readonly baseMaxHp: number;
  private relicMaxHpDelta = 0;
  private relicMaterialGainBonus = 0;
  private relicXpGainBonus = 0;
  private materialGainCarry = 0;
  private xpGainCarry = 0;
  private pendingLevelUps = 0;
  private readonly upgradeModifiers: UpgradeModifiers = {
    damageBonus: 0,
    attackRangeBonus: 0,
    attackSpeedBonus: 0,
    moveSpeedBonus: 0,
    maxHpDelta: 0,
    pickupRangeBonus: 0,
    critChanceBonus: 0,
  };
  private phase: RunPhase = 'ready';
  private readonly stats: RunStats = {
    damageDealt: 0,
    damageTaken: 0,
    hitsTaken: 0,
    materialsPicked: 0,
    enemiesDefeated: 0,
    levelsGained: 0,
  };
  private readonly deathStats: DeathStats = {
    collisionHits: 0,
    shockwaveHits: 0,
    bossHazardHits: 0,
    bossProjectileHits: 0,
    minionHits: 0,
    lastHitSource: 'none',
  };
  private readonly runStats = new RunStatsTracker();

  wave = 1;
  waveTimeLeft = getWaveDuration(1);
  hp: number;
  materials = 0;
  level = 1;
  xp = 0;

  constructor(
    readonly seed: string,
    private readonly playerTemplate: PlayerTemplateDefinition = getDefaultPlayerTemplate(),
  ) {
    this.baseMaxHp = Math.max(1, MAX_HP + this.playerTemplate.modifiers.maxHpDelta + getTunables().player.maxHpBonus);
    this.hp = this.getMaxHp();
  }

  start() {
    if (this.phase !== 'ready' && this.phase !== 'waveComplete') {
      return;
    }
    this.phase = 'playing';
  }

  update(deltaSeconds: number) {
    if (this.phase !== 'playing') {
      return;
    }

    this.waveTimeLeft = Math.max(0, this.waveTimeLeft - deltaSeconds);
    if (this.waveTimeLeft === 0) {
      this.completeWave();
    }
  }

  applyDamage(amount: number, source: DamageSource = 'collision') {
    if (this.phase !== 'playing') {
      return false;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`Invalid damage amount: ${amount}`);
    }

    const actualDamage = Math.min(this.hp, amount);
    this.hp -= actualDamage;
    this.stats.damageTaken += actualDamage;
    this.stats.hitsTaken += 1;
    this.recordDamageSource(source);
    this.runStats.recordDamageTaken(source, actualDamage);

    if (this.hp === 0) {
      this.phase = 'dead';
    }
    return true;
  }

  recordDamageDealt(amount: number, source?: PartnerType | 'relic') {
    if (!Number.isFinite(amount) || amount < 0) {
      throw new Error(`Invalid dealt damage amount: ${amount}`);
    }
    this.stats.damageDealt += amount;
    this.runStats.recordDamage(normalizeDamageSource(source), amount);
  }

  recordEnemyDefeated(source?: PartnerType | 'relic') {
    this.stats.enemiesDefeated += 1;
    this.runStats.recordKill(normalizeDamageSource(source));
  }

  recordRunPurchase(event: Omit<RunPurchaseEvent, 'index'>) {
    this.runStats.recordPurchase(event);
  }

  recordBuildSnapshot(snapshot: Omit<RunBuildSnapshot, 'index'>) {
    this.runStats.recordBuildSnapshot(snapshot);
  }

  getRunStats() {
    return this.runStats.snapshot();
  }

  recordDamageSourceKill(source: RunDamageSource) {
    this.runStats.recordKill(source);
  }

  recordMaterialPickup(amount = 1) {
    if (!Number.isInteger(amount) || amount <= 0) {
      throw new Error(`Invalid material pickup amount: ${amount}`);
    }
    const gained = this.applyMaterialGain(amount);
    this.stats.materialsPicked += gained;
    this.materials += gained;
    this.addXp(this.getXpGain(gained));
  }

  spendMaterials(amount: number) {
    if (!Number.isInteger(amount) || amount < 0) {
      throw new Error(`Invalid material spend amount: ${amount}`);
    }
    if (this.materials < amount) {
      return false;
    }
    this.materials -= amount;
    return true;
  }

  addMaterials(amount: number) {
    if (!Number.isInteger(amount) || amount < 0) {
      throw new Error(`Invalid material gain amount: ${amount}`);
    }
    this.materials += amount;
  }

  addAttackRangeUpgrade(amount = 0.15) {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`Invalid attack range upgrade amount: ${amount}`);
    }
    this.upgradeModifiers.attackRangeBonus += amount;
  }

  getAttackRangeBonus() {
    return this.upgradeModifiers.attackRangeBonus;
  }

  applyUpgrade(id: UpgradeId) {
    if (this.phase !== 'levelUp') {
      throw new Error(`Cannot apply upgrade while phase is ${this.phase}`);
    }

    this.applyUpgradeBonus(id);
  }

  applyUpgradeBonus(id: UpgradeId) {
    if (id === 'range') this.upgradeModifiers.attackRangeBonus += 0.15;
    if (id === 'damage') this.upgradeModifiers.damageBonus += 0.1;
    if (id === 'attack-speed') this.upgradeModifiers.attackSpeedBonus += 0.08;
    if (id === 'move-speed') this.upgradeModifiers.moveSpeedBonus += 0.08;
    if (id === 'pickup-range') this.upgradeModifiers.pickupRangeBonus += 0.2;
    if (id === 'crit-chance') this.upgradeModifiers.critChanceBonus += 0.05;
    if (id === 'scatter-shot') {
      this.upgradeModifiers.damageBonus += 0.08;
      this.upgradeModifiers.attackSpeedBonus += 0.1;
      this.upgradeModifiers.attackRangeBonus += 0.1;
    }
    if (id === 'blood-contract') {
      this.upgradeModifiers.damageBonus += 0.25;
      this.upgradeModifiers.critChanceBonus += 0.08;
      this.upgradeModifiers.maxHpDelta -= 10;
      this.hp = Math.min(this.getMaxHp(), this.hp);
    }
    if (id === 'treasure-map') {
      this.materials += 30;
      this.upgradeModifiers.pickupRangeBonus += 0.1;
    }
    if (id === 'magnet-ritual') {
      this.upgradeModifiers.pickupRangeBonus += 0.45;
      this.upgradeModifiers.moveSpeedBonus += 0.05;
    }
    if (id === 'sniper-oath') {
      this.upgradeModifiers.attackRangeBonus += 0.35;
      this.upgradeModifiers.damageBonus += 0.1;
      this.upgradeModifiers.attackSpeedBonus -= 0.04;
    }
    if (id === 'war-drum') {
      this.upgradeModifiers.attackSpeedBonus += 0.18;
      this.upgradeModifiers.moveSpeedBonus += 0.06;
    }
    if (id === 'survival-pact') {
      const oldMaxHp = this.getMaxHp();
      this.upgradeModifiers.maxHpDelta += 10;
      this.hp += this.getMaxHp() - oldMaxHp + 25;
      this.hp = Math.min(this.getMaxHp(), this.hp);
    }
    if (id === 'crit-engine') {
      this.upgradeModifiers.critChanceBonus += 0.12;
      this.upgradeModifiers.attackSpeedBonus += 0.08;
    }
    if (id === 'max-hp') {
      const oldMaxHp = this.getMaxHp();
      this.upgradeModifiers.maxHpDelta += 12;
      this.hp += this.getMaxHp() - oldMaxHp;
    }
  }

  addBonusXp(amount: number) {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`Invalid bonus XP amount: ${amount}`);
    }
    this.addXp(Math.floor(amount));
  }

  consumeLevelUp() {
    if (this.phase !== 'levelUp') {
      throw new Error(`Cannot consume level-up while phase is ${this.phase}`);
    }
    if (this.pendingLevelUps <= 0) {
      throw new Error('No pending level-up to consume');
    }

    this.pendingLevelUps -= 1;
    if (this.pendingLevelUps === 0) {
      this.phase = 'playing';
    }
    return this.pendingLevelUps;
  }

  heal(amount: number) {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`Invalid heal amount: ${amount}`);
    }
    if (this.phase !== 'playing') {
      return false;
    }
    this.hp = Math.min(this.getMaxHp(), this.hp + amount);
    return true;
  }

  recover(amount: number) {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`Invalid recovery amount: ${amount}`);
    }
    if (this.phase === 'dead') {
      return false;
    }
    this.hp = Math.min(this.getMaxHp(), this.hp + amount);
    return true;
  }

  setRelicModifiers(modifiers: RunRelicModifiers) {
    if (!Number.isFinite(modifiers.maxHpDelta)) {
      throw new Error(`Invalid relic max HP delta: ${modifiers.maxHpDelta}`);
    }
    const oldMaxHp = this.getMaxHp();
    this.relicMaxHpDelta = modifiers.maxHpDelta;
    this.relicMaterialGainBonus = modifiers.materialGainBonus;
    this.relicXpGainBonus = modifiers.xpGainBonus;
    const newMaxHp = this.getMaxHp();

    if (newMaxHp > oldMaxHp) {
      this.hp += newMaxHp - oldMaxHp;
    }
    this.hp = Math.min(newMaxHp, this.hp);
  }

  getNextLevelXp() {
    return getNextLevelXp(this.level);
  }

  nextWave() {
    if (this.phase !== 'waveComplete') {
      return false;
    }
    if (!isEndlessRun() && this.wave >= MAX_WAVE) {
      this.phase = 'victory';
      return false;
    }
    this.wave += 1;
    this.waveTimeLeft = getWaveDuration(this.wave);
    this.phase = this.pendingLevelUps > 0 ? 'levelUp' : 'playing';
    return true;
  }

  restart() {
    this.phase = 'ready';
    this.wave = 1;
    this.waveTimeLeft = getWaveDuration(1);
    this.hp = this.getMaxHp();
    this.materials = 0;
    this.level = 1;
    this.xp = 0;
    this.materialGainCarry = 0;
    this.xpGainCarry = 0;
    this.pendingLevelUps = 0;
    this.upgradeModifiers.damageBonus = 0;
    this.upgradeModifiers.attackRangeBonus = 0;
    this.upgradeModifiers.attackSpeedBonus = 0;
    this.upgradeModifiers.moveSpeedBonus = 0;
    this.upgradeModifiers.maxHpDelta = 0;
    this.upgradeModifiers.pickupRangeBonus = 0;
    this.upgradeModifiers.critChanceBonus = 0;
    this.stats.damageDealt = 0;
    this.stats.damageTaken = 0;
    this.stats.hitsTaken = 0;
    this.stats.materialsPicked = 0;
    this.stats.enemiesDefeated = 0;
    this.stats.levelsGained = 0;
    this.deathStats.collisionHits = 0;
    this.deathStats.shockwaveHits = 0;
    this.deathStats.bossHazardHits = 0;
    this.deathStats.bossProjectileHits = 0;
    this.deathStats.minionHits = 0;
    this.deathStats.lastHitSource = 'none';
    this.runStats.reset();
  }

  markVictory() {
    if (this.phase === 'dead') {
      return false;
    }
    this.phase = 'victory';
    return true;
  }

  getPhase() {
    return this.phase;
  }

  snapshot(): RunSnapshot {
    return {
      phase: this.phase,
      seed: this.seed,
      templateName: this.playerTemplate.name,
      wave: this.wave,
      maxWave: MAX_WAVE,
      waveTimeLeft: this.waveTimeLeft,
      maxHp: this.getMaxHp(),
      hp: this.hp,
      materials: this.materials,
      level: this.level,
      xp: this.xp,
      nextLevelXp: this.getNextLevelXp(),
      stats: { ...this.stats },
      deathStats: { ...this.deathStats },
      analysis: this.runStats.snapshot(),
      pendingLevelUps: this.pendingLevelUps,
      upgradeModifiers: { ...this.upgradeModifiers },
    };
  }

  private completeWave() {
    this.phase = !isEndlessRun() && this.wave >= MAX_WAVE ? 'victory' : 'waveComplete';
  }

  private addXp(amount: number) {
    this.xp += amount;
    while (this.xp >= this.getNextLevelXp()) {
      this.xp -= this.getNextLevelXp();
      this.level += 1;
      this.stats.levelsGained += 1;
      this.hp = Math.min(this.getMaxHp(), this.hp + getTunables().waves.levelUpHeal);
      this.pendingLevelUps += 1;
    }
    if (this.pendingLevelUps > 0 && this.phase === 'playing') {
      this.phase = 'levelUp';
    }
  }

  private applyMaterialGain(amount: number) {
    const raw = amount * this.playerTemplate.modifiers.materialGainMultiplier * (1 + this.relicMaterialGainBonus) + this.materialGainCarry;
    const gained = Math.floor(raw);
    this.materialGainCarry = raw - gained;
    return Math.max(1, gained);
  }

  private getXpGain(amount: number) {
    const raw = amount * (1 + this.relicXpGainBonus) + this.xpGainCarry;
    const gained = Math.floor(raw);
    this.xpGainCarry = raw - gained;
    return Math.max(1, gained);
  }

  private getMaxHp() {
    return Math.max(1, this.baseMaxHp + this.upgradeModifiers.maxHpDelta + this.relicMaxHpDelta);
  }

  private recordDamageSource(source: DamageSource) {
    this.deathStats.lastHitSource = source;
    if (source === 'shockwave') {
      this.deathStats.shockwaveHits += 1;
      return;
    }
    if (source === 'minion') {
      this.deathStats.minionHits += 1;
      return;
    }
    if (source === 'boss-hazard') {
      this.deathStats.bossHazardHits += 1;
      return;
    }
    if (source === 'boss-projectile') {
      this.deathStats.bossProjectileHits += 1;
      return;
    }
    this.deathStats.collisionHits += 1;
  }
}

export function getWaveDuration(wave: number) {
  if (!Number.isInteger(wave) || wave < 1) {
    throw new Error(`Invalid wave number: ${wave}`);
  }
  const waves = getTunables().waves;
  return Math.min(waves.maxDurationSeconds, waves.firstDurationSeconds + (wave - 1) * waves.durationStepSeconds);
}

export function isEndlessRun() {
  return MAX_WAVE === 0;
}

export function getNextLevelXp(level: number) {
  if (!Number.isInteger(level) || level < 1) {
    throw new Error(`Invalid level: ${level}`);
  }
  return FIRST_LEVEL_XP + (level - 1) * LEVEL_XP_STEP;
}
